import {
    useEffect,
    useRef,
    useMemo
} from 'react';

import {
    Marker,
    Popup
} from 'react-leaflet';

import {
    Box,
    Typography
} from '@mui/material';

import DirectionsBusIcon from '@mui/icons-material/DirectionsBus';

import { useMapStore } from '../../stores/map/useMapStore';

import { createVehiclePositionIcon } from '../../utils/map/createVehiclePositionIcon';

import { formatDateTime } from '../../utils/dateUtils';

const vehicleIcon = createVehiclePositionIcon();

const tripVehicleIcon = createVehiclePositionIcon('#ed6c02');

export default function MapPezinhoVehiclesMarkers() {

    const tripPlate =
        useMapStore(
            state =>
                String(
                    state.tripData?.placa || ''
                ).trim()
        );

    const markerRefs = useRef({});

    const selectedPositions =
        useMapStore(
            state =>
                state.pezinho.selectedPositions
        );

    const selectedVehicles =
        useMapStore(
            state =>
                state.pezinho.selectedVehicles
        );

    const visiblePositions = useMemo(() => {

        const selectedVehiclesSet =
            new Set(
                selectedVehicles || []
            );

        return (selectedPositions || [])
            .filter(position => {

                const placa =
                    String(
                        position?.placa || ''
                    ).trim();

                return selectedVehiclesSet.has(
                    placa
                );

            });

    }, [
        selectedPositions,
        selectedVehicles
    ]);

    useEffect(() => {

        // =========================
        // FECHA POPUPS
        // =========================

        Object.values(
            markerRefs.current
        ).forEach(marker => {

            marker?.closePopup();

        });

        // =========================
        // SOMENTE UMA POSIÇÃO VISÍVEL
        // =========================

        if (visiblePositions.length !== 1) {
            return;
        }

        const position =
            visiblePositions[0];

        const marker =
            markerRefs.current[
                position.POSITION_UID
            ];

        if (!marker) {
            return;
        }

        marker.openPopup();

    }, [visiblePositions]);

    if (!visiblePositions?.length) {
        return null;
    }

    return (
        <>
            {visiblePositions.map(
                (position) => {

                    const lat =
                        Number(
                            position?.latitude
                        );

                    const lng =
                        Number(
                            position?.longitude
                        );

                    if (
                        !Number.isFinite(lat) ||
                        !Number.isFinite(lng)
                    ) {
                        return null;
                    }

                    const placa =
                        String(
                            position?.placa || ''
                        ).trim();

                    const isTripVehicle =
                        !!tripPlate &&
                        placa === tripPlate;

                    return (
                        <Marker
                            ref={(marker) => {

                                const uuid =
                                    position.POSITION_UID;

                                if (marker) {
                                    markerRefs.current[uuid] =
                                        marker;
                                } else {
                                    delete markerRefs.current[uuid];
                                }

                            }}
                            key={position.POSITION_UID}
                            position={[
                                lat,
                                lng
                            ]}
                            icon={
                                isTripVehicle
                                    ? tripVehicleIcon
                                    : vehicleIcon
                            }
                        >
                            <Popup
                                className="pezinho-vehicle-popup"
                                minWidth={120}
                                maxWidth={160}
                            >
                                <Box
                                    sx={{
                                        minWidth: 110
                                    }}
                                >
                                    <Box
                                        sx={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: 0.5,
                                            mb: 0.25
                                        }}
                                    >
                                        <DirectionsBusIcon
                                            sx={{
                                                fontSize: 16
                                            }}
                                            color={
                                                isTripVehicle
                                                    ? 'warning'
                                                    : 'primary'
                                            }
                                        />

                                        <Typography
                                            variant="caption"
                                            fontWeight={600}
                                        >
                                            {placa}
                                        </Typography>
                                    </Box>

                                    <Typography
                                        variant="caption"
                                        color="text.secondary"
                                        display="block"
                                        sx={{
                                            fontSize: '0.7rem',
                                            lineHeight: 1.4
                                        }}
                                    >
                                        {formatDateTime(
                                            position.dataHora,
                                            true
                                        )}
                                    </Typography>

                                    <Typography
                                        variant="caption"
                                        color="text.secondary"
                                        display="block"
                                        sx={{
                                            fontSize: '0.7rem',
                                            lineHeight: 1.4
                                        }}
                                    >
                                        Distância:{' '}
                                        {Math.round(
                                            position.distance
                                        )} m
                                    </Typography>
                                </Box>
                            </Popup>
                        </Marker>
                    );

                }
            )}
        </>
    );
}